import { minBy } from 'es-toolkit/compat';

import { PublicationType } from ':modules/posts';

const ASPECT_RATIOS_FOR_SEO_POST = {
    portrait: 3 / 4,
    landscape: 4 / 3,
    square: 1,
};
const ASPECT_RATIOS_FOR_SOCIAL_POST = {
    portrait: 4 / 5,
    landscape: 16 / 9,
    square: 1,
};
const ASPECT_RATIO_FOR_REEL = 9 / 16;
const ASPECT_RATIO_FOR_STORY = 9 / 16;

interface Area {
    left: number;
    top: number;
    width: number;
    height: number;
}

export class MediaCropComputer {
    static computePreferredAspectRatioFor(
        params:
            | { publicationType: PublicationType.POST; type: 'social'; originalAspectRatio: number }
            | { publicationType: PublicationType.POST; type: 'seo'; originalAspectRatio: number }
            | { publicationType: PublicationType.REEL | PublicationType.STORY }
    ): number {
        switch (params.publicationType) {
            case PublicationType.POST:
                if (params.type === 'seo') {
                    return MediaCropComputer._computePreferredAspectRatioForSeoPost(params.originalAspectRatio);
                }
                if (params.type === 'social') {
                    return MediaCropComputer._computePreferredAspectRatioForSocialPost(params.originalAspectRatio);
                }
                throw new Error('Invalid post type');
            case PublicationType.REEL:
                return ASPECT_RATIO_FOR_REEL;
            case PublicationType.STORY:
                return ASPECT_RATIO_FOR_STORY;
            default:
                throw new Error('Unknown publication type');
        }
    }

    private static _computePreferredAspectRatioForSocialPost(originalAspectRatio: number): number {
        const squareTolerance = 0.01;
        const isCloseToSquare = Math.abs(ASPECT_RATIOS_FOR_SOCIAL_POST.square - originalAspectRatio) < squareTolerance;
        if (isCloseToSquare) {
            return 1;
        }

        const { portrait, landscape } = ASPECT_RATIOS_FOR_SOCIAL_POST;
        const preferredAspectRatio = minBy([portrait, landscape], (e) => Math.abs(originalAspectRatio - e));

        if (!preferredAspectRatio) {
            throw new Error('[MediaCropComputer._computePreferredAspectRatioForSocialPost] preferredAspectRatio is null');
        }

        return preferredAspectRatio;
    }

    private static _computePreferredAspectRatioForSeoPost(originalAspectRatio: number): number {
        const { portrait, landscape, square } = ASPECT_RATIOS_FOR_SEO_POST;
        const preferredAspectRatio = minBy([portrait, landscape, square], (e) => Math.abs(originalAspectRatio - e));

        if (!preferredAspectRatio) {
            throw new Error('[MediaCropComputer._computePreferredAspectRatioForSeoPost] preferredAspectRatio is null');
        }

        return preferredAspectRatio;
    }

    static computeArea({ originalAspectRatio, targetAspectRatio }: { originalAspectRatio: number; targetAspectRatio: number }): Area {
        let left: number;
        let top: number;
        let width: number;
        let height: number;
        if (originalAspectRatio > targetAspectRatio) {
            top = 0;
            height = 1;
            width = targetAspectRatio / originalAspectRatio;
            left = (1 - width) / 2;
        } else if (originalAspectRatio < targetAspectRatio) {
            left = 0;
            width = 1;
            height = originalAspectRatio / targetAspectRatio;
            top = (1 - height) / 2;
        } else {
            top = 0;
            left = 0;
            width = 1;
            height = 1;
        }
        return {
            left,
            top,
            width,
            height,
        };
    }
}
